import { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface VideoHeroProps {
  videoSrc: string;
  poster?: string;
  title?: string;
  subtitle?: string;
  height?: string;
  children?: ReactNode;
  /** Pinned to the bottom edge of the hero */
  footer?: ReactNode;
}

export function VideoHero({ 
  videoSrc, 
  poster,
  title, 
  subtitle, 
  height,
  children,
  footer
}: VideoHeroProps) {
  const hasTextContent = title || subtitle;
  const isMobile = useMediaQuery('(max-width: 768px)');
  const shouldReduceMotion = useReducedMotion();
  const minHeight = isMobile ? 'min(70vh, 500px)' : 'min(85vh, 700px)';

  return (
    <section 
      className="relative w-full flex items-center justify-center bg-gradient-to-br from-brand-900 to-brand-700"
      // A fixed height let longer hero text spill under the next section on phones;
      // pages grow with their content unless they ask for an exact height (homepage full screen).
      style={height ? { minHeight, height } : { minHeight }}
    >
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
        <video
          autoPlay
          loop
          muted
          playsInline
          {...(poster && { poster })}
          className="w-full h-full object-cover motion-reduce:hidden"
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
        {/* Overlay - adjust opacity based on content */}
        <div className={`absolute inset-0 bg-gradient-to-b from-black/60 to-black/40 ${!hasTextContent ? 'opacity-30' : ''}`} />
      </div>
      
      {/* Content: on mobile use items-start + top padding so title is not clipped; from sm keep centered */}
      <div className="relative z-10 flex flex-col items-start justify-center w-full h-full px-4 pt-[max(5.5rem,calc(env(safe-area-inset-top)+4.5rem))] pb-8 sm:items-center sm:pt-8 sm:py-12">
        {title && (
          <motion.h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold text-white mb-3 sm:mb-6 text-center drop-shadow-lg max-w-4xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.5, delay: shouldReduceMotion ? 0 : 0.2 }}
          >
            {title}
          </motion.h1>
        )}
        
        {subtitle && (
          <motion.p 
            className={`text-lg sm:text-xl text-white/90 ${!title ? 'mt-0' : 'mb-4 sm:mb-6'} max-w-2xl mx-auto text-center drop-shadow-lg font-medium`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.5, delay: shouldReduceMotion ? 0 : (title ? 0.3 : 0.2) }}
          >
            {subtitle}
          </motion.p>
        )}
        
        {children && (
          <div className="w-full max-w-5xl px-2 sm:px-6">
            {children}
          </div>
        )}
      </div>

      {footer && (
        <div className="absolute inset-x-0 bottom-3 z-10 flex justify-center">
          {footer}
        </div>
      )}
    </section>
  );
} 